<template>
  <div>
    <template v-if="canViewBusinessAdmins">
      <b-row>
        <b-col>
          <div class="page-subtitle mt-5 mb-4">Business Admins</div>
          <p class="text-muted mb-3">
            Manage additional business administrators for your company account.
          </p>
        </b-col>
      </b-row>

      <div
        class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between mb-3"
        style="gap: 12px"
      >
        <div>
          <small class="font-weight-bold d-block mb-1">
            Search (Name, Email, Phone)
          </small>
          <b-form-input
            v-model="search"
            placeholder="Type to search"
            debounce="500"
            class="search-input"
          />
        </div>

        <div
          v-if="canManageBusinessAdmins"
          class="d-flex flex-column align-items-stretch align-items-sm-end add-ba-wrap"
        >
          <b-button
            variant="primary"
            class="add-ba-btn"
            @click="showMyModal = true"
            :disabled="!canAddMoreBas"
          >
            <!-- Shorter label on phones so it fits on one line -->
            <span class="d-sm-none">+ Add Business Admin</span>
            <span class="d-none d-sm-inline"
              >+ Add/Register Business Admin</span
            >
          </b-button>

          <h6
            v-if="!canAddMoreBas"
            class="text-danger mt-2 text-center text-sm-right"
          >
            You've added the maximum of 5 additional Business Admins.
          </h6>
        </div>
      </div>

      <b-modal
        v-model="showMyModal"
        centered
        title="Business Admin Details"
        hide-footer
        @hidden="resetForm"
      >
        <b-form @submit.prevent="submitForm">
          <b-form-group label="Business Admin Email">
            <b-form-input
              v-model="form.email"
              type="email"
              placeholder="Enter Email Address"
              required
            />
            <small v-if="emailInUseError" class="text-danger">{{
              emailInUseError
            }}</small>
          </b-form-group>

          <b-form-group label="Role">
            <b-form-select
              v-model="form.role_id"
              :options="roleOptions"
              :disabled="loadingRoles"
              required
            />
            <small
              v-if="selectedRole && selectedRole.description"
              class="text-muted d-block mt-1"
            >
              {{ selectedRole.description }}
            </small>
            <small class="text-muted d-block mt-1">
              Manage roles, their permissions and project access under
              <strong>My Account → Roles</strong>.
            </small>
            <small v-if="isProjectScopedRole" class="text-info d-block mt-1">
              This role only sees the projects selected on the role itself.
            </small>
          </b-form-group>

          <div class="d-flex justify-content-end mb-2">
            <b-button variant="invisible" @click="exitOff"> Cancel </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="!!emailInUseError || isCheckingEmail"
            >
              Submit
            </b-button>
          </div>
        </b-form>
      </b-modal>

      <!-- Reminder: assign a token so the new Business Admin can access the website -->
      <b-modal v-model="showTokenReminder" centered ok-only>
        <template #modal-title>
          <h3 class="font-weight-bold">
            {{ reclaimedToken ? 'Business Admin Added' : 'Assign a Token' }}
          </h3>
        </template>

        <p v-if="reclaimedToken" style="opacity: 1; font-size: 18px">
          This Business Admin's previous token has been reassigned to their
          account. Their access to TradiePass has been restored and no further
          action is required.
        </p>
        <p v-else style="opacity: 1; font-size: 18px">
          You've just added a new Business Admin. To give them access to
          TradiePass, you'll need to
          <span class="font-weight-bold">assign a token</span>
          to their account from the Business Admins list.
        </p>
      </b-modal>
      <company-business-admins-list
        ref="businessAdminsList"
        :search="search"
        :is-primary-business-admin="isPrimaryBusinessAdmin"
        @ba-count-changed="onBaCountChanged"
      />
    </template>

    <company-tradie-access-list v-if="canViewBusinessAdmins" />
  </div>
</template>

<script>
import { _ } from 'core-js'
import CompanyBusinessAdminsList from '../lists/CompanyBusinessAdminsList.vue'
import CompanyTradieAccessList from '../lists/CompanyTradieAccessList.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    CompanyBusinessAdminsList,
    CompanyTradieAccessList,
  },
  props: {
    isPrimaryBusinessAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMyModal: false,
      form: {
        email: '',
        role_id: null,
      },
      roles: [],
      loadingRoles: false,
      search: '',
      canAddMoreBas: true,
      showTokenReminder: false,
      reclaimedToken: false,

      emailInUseError: '',
      isCheckingEmail: false,
      emailLastChecked: '',
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    canViewBusinessAdmins() {
      return (
        this.isPrimaryBusinessAdmin ||
        this.hasPermission('settings.users.view') ||
        this.hasPermission('settings.users.manage')
      )
    },

    canManageBusinessAdmins() {
      return (
        this.isPrimaryBusinessAdmin ||
        this.hasPermission('settings.users.manage')
      )
    },

    roleOptions() {
      const opts = this.roles.map((r) => ({ value: r.id, text: r.name }))
      if (opts.length === 0) {
        return [{ value: null, text: 'No roles available', disabled: true }]
      }
      return opts
    },

    selectedRole() {
      return this.roles.find((r) => r.id === this.form.role_id) || null
    },

    /** Project picker shows only when the chosen role is project-scoped. */
    isProjectScopedRole() {
      return !!(this.selectedRole && this.selectedRole.is_project_scoped)
    },
  },

  watch: {
    showMyModal(open) {
      if (open) this.loadRoles()
    },
    'form.email': _.debounce(async function (newVal) {
      this.isCheckingEmail = true
      this.emailInUseError = ''
      try {
        await this.$api.post('auth/check-email', { email: newVal })
        this.emailInUseError =
          'This email is already used by a tradie or Business Admin'
      } catch (err) {
        const data = err && err.response && err.response.data
        const errorCode = data && data.error_code
        const message = data && data.message
        const emailIsFree =
          errorCode === 'EMAIL_NOT_FOUND' || errorCode === 'USERNAME_NOT_FOUND'
        if (emailIsFree) {
          this.emailInUseError = ''
        } else if (message) {
          this.emailInUseError = message
        }
      } finally {
        this.isCheckingEmail = false
      }
    }, 500),
  },

  methods: {
    async loadRoles() {
      this.loadingRoles = true
      try {
        const { data } = await this.$api.get('company/roles')
        const list = (data && data.data) || data
        this.roles = Array.isArray(list) ? list : []
        // Default to a sensible non-project-scoped role (the "admin" system role)
        // when nothing is selected yet.
        if (this.form.role_id == null && this.roles.length) {
          const adminRole =
            this.roles.find((r) => r.is_system && !r.is_project_scoped) ||
            this.roles.find((r) => !r.is_project_scoped) ||
            this.roles[0]
          this.form.role_id = adminRole.id
        }
      } catch (e) {
        this.roles = []
      } finally {
        this.loadingRoles = false
      }
    },

    async submitForm() {
      if (this.emailInUseError) return
      if (this.form.role_id == null) {
        this.emailInUseError = 'Please select a role.'
        return
      }

      const companyId = this.user.company.id || this.user.company_id

      try {
        const res = await this.$api.post('/company/invite', {
          email: this.form.email,
          company_id: companyId,
          // Project access is carried by the role, not the individual invite.
          role_id: this.form.role_id,
        })

        // A re-added BA whose previously-reserved token seat was auto-reassigned
        // doesn't need a token assigned, so show the "already has access" message
        // instead of the "assign a token" reminder.
        const body = (res && res.data) || {}
        this.reclaimedToken = !!(body.data && body.data.token_reclaimed)

        // Refresh the list so the new BA appears without pressing Refresh
        if (this.$refs.businessAdminsList) {
          this.$refs.businessAdminsList.loadBusinessAdmins()
        }

        // reset + close
        this.form = { email: '', role_id: null }
        this.showMyModal = false
        this.showTokenReminder = true
      } catch (err) {
        const data = err && err.response && err.response.data
        const errors = data && data.errors
        const message = data && data.message
        if (errors && errors.email && errors.email[0]) {
          this.emailInUseError = errors.email[0]
        } else if (message) {
          this.emailInUseError = message
        }
      }
    },

    exitOff() {
      this.showMyModal = false
    },

    resetForm() {
      this.form = { email: '', role_id: null }
      this.emailInUseError = ''
      this.isCheckingEmail = false
    },

    onBaCountChanged(count) {
      this.canAddMoreBas = count < 5
    },
  },
}
</script>

<style scoped>
.search-input {
  max-width: 260px;
}

/* ── Mobile responsiveness ──────────────────────────────────────────────
   On phones the search + action button stack (handled by the flex utilities
   in the template); let the search span the full width and scale the section
   heading spacing so it starts compact and builds up to the desktop value. */
@media (max-width: 575.98px) {
  .search-input {
    max-width: 100%;
  }
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
  /* Full-width action button that matches the search field. Slightly smaller
     text so the label fits on one line; if it ever can't, it wraps (centred)
     rather than clipping off the edge. */
  .add-ba-wrap {
    width: 100%;
  }
  .add-ba-btn {
    width: 100%;
    padding: 0.6rem 0.5rem;
    font-size: 0.9rem;
    white-space: normal;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
}
</style>
